if (!customElements.get('variant-popup-picker')) {
  customElements.define(
    'variant-popup-picker',
    class VariantPopupPicker extends HTMLElement {
      constructor() {
        super();
        this.isOpen = false;
        this.handleEscape = this.handleEscape.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
      }

      connectedCallback() {
        this.panel = this.querySelector('.variant-popup-panel');
        this.message = this.querySelector('[data-variant-popup-message]');
        this.closeButton = this.querySelector('[data-variant-popup-close]');
        this.sizeGuideButton = this.querySelector('[data-variant-size-guide]');
        this.optionButtons = Array.from(this.querySelectorAll('.variant-popup__option-btn'));
        this.optionButtonHandlers = [];
        this.requiresSelection = this.dataset.requiresSelection === 'true';
        this.hasExplicitVariant = this.dataset.hasExplicitVariant === 'true';
        this.placeholderText = '';
        this.boundSelect = null;
        this.boundForm = null;
        if (!this.panel) return;

        this.handleDocumentClick = (event) => {
          const trigger = event.target.closest('[data-variant-popup-open]');
          if (!trigger || trigger.id !== this.dataset.triggerId) return;
          event.preventDefault();
          this.open();
        };
        this.handleCloseClick = () => this.close();
        this.handleSizeGuideClick = () => {
          const sizeChartTrigger = document.querySelector('.size-chart-trigger, [id^="sizeChartTrigger"]');
          if (sizeChartTrigger) sizeChartTrigger.click();
        };
        this.handleSelectChange = () => this.syncFromSelect();

        this.refreshRefs();
        document.addEventListener('click', this.handleDocumentClick);
        this.closeButton?.addEventListener('click', this.handleCloseClick);
        this.sizeGuideButton?.addEventListener('click', this.handleSizeGuideClick);
        this.optionButtons.forEach((button) => {
          const optionButtonHandler = () => this.selectOption(button.dataset.optionValue);
          this.optionButtonHandlers.push({ button, optionButtonHandler });
          button.addEventListener('click', optionButtonHandler);
        });

        this.bindSelectListener();
        this.bindFormListener();
        this.syncFromSelect();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleDocumentClick);
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        this.sizeGuideButton?.removeEventListener('click', this.handleSizeGuideClick);
        this.boundSelect?.removeEventListener('change', this.handleSelectChange);
        this.boundForm?.removeEventListener('submit', this.handleFormSubmit, true);
        this.optionButtonHandlers?.forEach(({ button, optionButtonHandler }) => {
          button.removeEventListener('click', optionButtonHandler);
        });
        document.removeEventListener('keydown', this.handleEscape);
      }

      syncFromSelect() {
        this.refreshRefs();
        if (!this.select) return;
        const currentValue = this.select.value;
        const shouldShowPlaceholder = this.requiresSelection && !this.isSelectionChosen();
        const displayValue = shouldShowPlaceholder ? this.placeholderText : currentValue || this.placeholderText;
        if (!shouldShowPlaceholder) this.hideMessage();

        if (this.valueDisplay) {
          this.valueDisplay.textContent = displayValue;
        }

        this.optionButtons.forEach((button) => {
          const selected = !shouldShowPlaceholder && button.dataset.optionValue === currentValue;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
      }

      selectOption(value) {
        this.refreshRefs();
        if (!value || this.select.value === value) {
          this.hasExplicitVariant = true;
          this.hideMessage();
          this.syncFromSelect();
          this.close();
          return;
        }

        this.hasExplicitVariant = true;
        this.hideMessage();
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
      }

      handleFormSubmit(event) {
        this.refreshRefs();
        if (!this.requiresSelection || this.isSelectionChosen()) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.showMessage();
        this.open();
      }

      open() {
        this.refreshRefs();
        this.isOpen = true;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        this.trigger?.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', this.handleEscape);
      }

      close() {
        this.refreshRefs();
        this.isOpen = false;
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        this.trigger?.setAttribute('aria-expanded', 'false');
        document.removeEventListener('keydown', this.handleEscape);
      }

      handleEscape(event) {
        if (event.key === 'Escape' && this.isOpen) this.close();
      }

      showMessage() {
        if (!this.message) return;
        this.message.hidden = false;
      }

      hideMessage() {
        if (!this.message) return;
        this.message.hidden = true;
      }

      selectHasNonPlaceholderValue() {
        this.refreshRefs();
        if (!this.select) return false;
        const selectedOption = this.select.options[this.select.selectedIndex];
        if (!selectedOption) return false;
        const selectedText = selectedOption.textContent?.trim().toLowerCase() || '';
        const placeholderText = this.placeholderText.trim().toLowerCase();
        return selectedOption.value !== '' && selectedText !== placeholderText;
      }

      isSelectionChosen() {
        if (!this.requiresSelection) return true;
        if (this.hasExplicitVariant) return true;
        if (this.selectHasNonPlaceholderValue()) return true;
        const displayText = this.valueDisplay?.textContent?.trim().toLowerCase() || '';
        return displayText !== '' && displayText !== this.placeholderText.trim().toLowerCase();
      }

      refreshRefs() {
        this.trigger = document.getElementById(this.dataset.triggerId);
        this.valueDisplay = document.getElementById(this.dataset.valueId);
        this.select = document.getElementById(this.dataset.selectId);
        this.form = this.select?.form || null;
        this.placeholderText = this.valueDisplay?.dataset.placeholderText || '';
        this.bindSelectListener();
        this.bindFormListener();
      }

      bindSelectListener() {
        if (this.boundSelect === this.select) return;
        this.boundSelect?.removeEventListener('change', this.handleSelectChange);
        this.boundSelect = this.select || null;
        this.boundSelect?.addEventListener('change', this.handleSelectChange);
      }

      bindFormListener() {
        if (this.boundForm === this.form) return;
        this.boundForm?.removeEventListener('submit', this.handleFormSubmit, true);
        this.boundForm = this.form || null;
        this.boundForm?.addEventListener('submit', this.handleFormSubmit, true);
      }
    }
  );
}
